import type {
  AuthenticationResponseJSON,
  PublicKeyCredentialCreationOptionsJSON,
  PublicKeyCredentialRequestOptionsJSON,
  RegistrationResponseJSON,
} from "@simplewebauthn/browser";

import { noteRequest } from "./connection.js";
import type {
  EventRecord,
  EventWindow,
  Me,
  OccurrenceRecord,
  Person,
} from "./types.js";

/**
 * The API, typed.
 *
 * Errors carry the server's message so the interface can show what happened
 * and what fixes it, rather than a generic apology.
 */

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly code?: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export class NotSignedIn extends ApiError {
  constructor() {
    super(401, "Sign in to see this.", "not_signed_in");
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  let response: Response;

  try {
    response = await fetch(path, {
      headers: init.body === undefined ? {} : { "Content-Type": "application/json" },
      ...init,
    });
  } catch {
    // The service worker may still answer from cache, in which case this never
    // runs. Reaching here means there was nothing cached either.
    noteRequest(false);
    throw new ApiError(0, "Frankie can't be reached right now.", "offline");
  }

  /*
   * A response served from the cache carries this header, added by the service
   * worker's NetworkFirst strategy falling back. Anything else means the
   * network answered.
   */
  noteRequest(!response.headers.has("x-frankie-cached"));

  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as
      | { message?: string; error?: string }
      | null;

    // A 401 is "not signed in" unless it says otherwise — a refused passkey
    // is also a 401, and its message is the one worth showing.
    if (response.status === 401 && (body?.error ?? "not_signed_in") === "not_signed_in") {
      throw new NotSignedIn();
    }

    throw new ApiError(
      response.status,
      body?.message ?? "That didn't work.",
      body?.error,
    );
  }

  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

export interface AccessToken {
  id: string;
  kind: "display" | "ics";
  label: string;
  token: string | null;
  person_id: string | null;
  created_at: string;
}

export interface Passkey {
  id: string;
  label: string;
  created_at: string;
  last_used_at: string | null;
}

/** Which occurrences a change applies to. */
export type Scope = "this" | "this_and_future";

export interface EventDraftPayload {
  title: string;
  starts_at: string;
  ends_at?: string | null;
  all_day: boolean;
  notes?: string | null;
  location?: string | null;
  rrule?: string | null;
  person_ids: string[];
}

/** One Skylight category's fate, as decided on the import screen. */
export interface CategoryRule {
  person?: string | null | undefined;
  skip?: boolean | undefined;
  background?: boolean | undefined;
  person_from_title?: boolean | undefined;
  note?: string | undefined;
}

export interface Mapping {
  sources: string[];
  categories: Record<string, CategoryRule>;
}

export interface ImportPreview {
  mapping: Mapping;
  proposed: Mapping;
  sources: string[];
  /** Categories Frankie would not guess at. */
  unresolved: string[];
  counts: { total: number; importing: number; skipping: number };
  per_person: { name: string; count: number }[];
  /** Events whose times don't look plausible — reported, never corrected. */
  suspicious: { title: string; starts_at: string; reason: string }[];
  skipped_reasons: { reason: string; count: number }[];
  sample: {
    title: string | null;
    starts_at: string | null;
    /** The last day covered for an all-day event; the finishing instant for a
        timed one. Whatever the import is about to write. */
    ends: string | null;
    all_day: boolean;
    rrule: string | null;
    people: string[];
    background: boolean;
  }[];
}

export interface ImportResult {
  inserted: number;
  updated: number;
  skipped: number;
  failed: { title: string; error: string }[];
}

export const api = {
  me: () => request<Me>("/api/me"),

  requestLink: (email: string) =>
    request<void>("/api/auth/request-link", {
      method: "POST",
      body: JSON.stringify({ email }),
    }),

  logout: () => request<void>("/api/auth/logout", { method: "POST" }),

  passkeys: () => request<Passkey[]>("/api/auth/passkeys"),

  deletePasskey: (id: string) =>
    request<void>(`/api/auth/passkeys/${encodeURIComponent(id)}`, { method: "DELETE" }),

  passkeyRegisterOptions: () =>
    request<PublicKeyCredentialCreationOptionsJSON>("/api/auth/passkey/register/options", {
      method: "POST",
    }),

  passkeyRegisterVerify: (response: RegistrationResponseJSON) =>
    request<Passkey>("/api/auth/passkey/register/verify", {
      method: "POST",
      body: JSON.stringify(response),
    }),

  passkeyLoginOptions: () =>
    request<PublicKeyCredentialRequestOptionsJSON>("/api/auth/passkey/login/options", {
      method: "POST",
    }),

  passkeyLoginVerify: (response: AuthenticationResponseJSON) =>
    request<void>("/api/auth/passkey/login/verify", {
      method: "POST",
      body: JSON.stringify(response),
    }),

  events: (from: string, to: string) =>
    request<EventWindow>(`/api/events?from=${from}&to=${to}`),

  event: (id: string) => request<EventRecord>(`/api/events/${id}`),

  /**
   * One occurrence, with its exception applied.
   *
   * Not the same thing as the event: a "just this one" edit lives in an
   * exception row, so the series still carries the old title. Any screen
   * showing a particular day has to ask for that day.
   */
  occurrence: (id: string, occurrenceId: string) =>
    request<OccurrenceRecord>(
      `/api/events/${id}/occurrences/${encodeURIComponent(occurrenceId)}`,
    ),

  createEvent: (payload: EventDraftPayload) =>
    request<EventRecord>("/api/events", { method: "POST", body: JSON.stringify(payload) }),

  updateEvent: (id: string, payload: Partial<EventDraftPayload>) =>
    request<EventRecord>(`/api/events/${id}`, {
      method: "PATCH",
      body: JSON.stringify(payload),
    }),

  deleteEvent: (id: string) => request<void>(`/api/events/${id}`, { method: "DELETE" }),

  deleteOccurrence: (id: string, occurrenceId: string, scope: Scope) =>
    request<void>(
      `/api/events/${id}/occurrences/${encodeURIComponent(occurrenceId)}?scope=${scope}`,
      { method: "DELETE" },
    ),

  updateOccurrence: (
    id: string,
    occurrenceId: string,
    scope: Scope,
    payload: { title?: string; starts_at?: string; ends_at?: string | null; notes?: string | null },
  ) =>
    request<void>(`/api/events/${id}/occurrences/${encodeURIComponent(occurrenceId)}`, {
      method: "PATCH",
      body: JSON.stringify({ scope, ...payload }),
    }),

  people: () => request<Person[]>("/api/people"),

  tokens: () => request<AccessToken[]>("/api/tokens"),

  personFeed: (personId: string) =>
    request<{ token: string }>(`/api/people/${personId}/feed`, { method: "POST" }),

  rotateToken: (kind: "display" | "ics") =>
    request<{ kind: string; token: string }>(`/api/tokens/${kind}/rotate`, { method: "POST" }),

  updatePerson: (id: string, payload: { display_name?: string; color?: string }) =>
    request<Person>(`/api/people/${id}`, { method: "PATCH", body: JSON.stringify(payload) }),

  createPerson: (payload: { display_name: string; color: string; role?: string | null }) =>
    request<Person>("/api/people", { method: "POST", body: JSON.stringify(payload) }),

  deletePerson: (id: string) => request<void>(`/api/people/${id}`, { method: "DELETE" }),

  importPreview: (events: unknown[], mapping: Mapping | null) =>
    request<ImportPreview>("/api/import/skylight/preview", {
      method: "POST",
      body: JSON.stringify({ events, mapping }),
    }),

  importSkylight: (events: unknown[], mapping: Mapping) =>
    request<ImportResult>("/api/import/skylight", {
      method: "POST",
      body: JSON.stringify({ events, mapping }),
    }),

  removeImported: () =>
    request<{ removed: number }>("/api/import/skylight", { method: "DELETE" }),
};
